// Libs
import classNames from 'classnames/bind';
import { Box, Checkbox, FormControlLabel, Grid2, Rating, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoadScript } from '@react-google-maps/api';
// Components, Layouts, Pages
import { BaseAvatarGroup, BaseLabelValueField, BaseMap, BaseTextEditor } from '~/components/common';
// Others
import { DEFAULT_GG_MAP_LOAD_SCRIPT_LIB, DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { googleMapApiKey } from '~/utils/constants/env';
import {
  displayPercentageVal,
  formatDateByTimeZone,
  formatPhoneNumber,
  formatTitleCase,
  getFullName,
} from '~/utils/helper/common';
import { selectJobDetail } from '~/thunks/job/jobSlice';
import {
  DateFormatEnum,
  ModeTextEditorEnum,
  ThemeTextEditorEnum,
  UnitJobTypeEnum,
} from '~/utils/enum/common';
// Styles, images, icons
import styles from './JobSummary.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const JobSummary = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job']);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });
  //#endregion Declare Hook

  //#region Selector
  const jobDetail = useSelector(selectJobDetail);
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Box component={'div'} id='jobSummaryPage' className={cx('container')}>
      <Grid2 container spacing={'24px'} component={'div'} className={cx('body')}>
        <Grid2 size={6}>
          <BaseLabelValueField label={t('tab_summary_job_name')} value={jobDetail?.name} />

          <BaseLabelValueField
            label={t('tab_summary_primary_contact')}
            value={
              <Stack spacing={'8px'}>
                <Typography component={'p'} className={cx('textValue')}>
                  {getFullName({
                    firstName: jobDetail?.customer?.firstName,
                    lastName: jobDetail?.customer?.lastName,
                  })}
                </Typography>

                <Typography component={'p'} className={cx('textValue')}>
                  {formatPhoneNumber(jobDetail?.customer?.phoneNumber)}
                </Typography>

                <Typography component={'p'} className={cx('textValue')}>
                  {jobDetail?.customer?.email || EMPTY_STRING}
                </Typography>
              </Stack>
            }
          />

          <BaseLabelValueField
            label={t('tab_summary_schedule')}
            value={
              <Stack spacing={'8px'}>
                <Box component={'div'} className={cx('viewFrom')}>
                  <Typography component={'p'} className={cx('textValueFrom')}>
                    {t('tab_summary_from_label')}
                  </Typography>
                  <Typography component={'p'} className={cx('textValue')}>
                    {jobDetail?.schedule?.from
                      ? formatDateByTimeZone(
                          jobDetail.schedule.from,
                          DateFormatEnum.DD_MMM_YYYY_H_MM_A,
                          jobDetail?.timeZone
                        )
                      : EMPTY_STRING}
                  </Typography>
                </Box>

                <Box component={'div'} className={cx('viewFrom')}>
                  <Typography component={'p'} className={cx('textValueFrom')}>
                    {t('tab_summary_to_label')}
                  </Typography>
                  <Typography component={'p'} className={cx('textValue')}>
                    {jobDetail?.schedule?.to
                      ? formatDateByTimeZone(
                          jobDetail.schedule.to,
                          DateFormatEnum.DD_MMM_YYYY_H_MM_A,
                          jobDetail?.timeZone
                        )
                      : EMPTY_STRING}
                  </Typography>
                </Box>

                {jobDetail?.timeZone && (
                  <Typography component={'p'} className={cx('textValue')}>
                    {jobDetail?.timeZone}
                  </Typography>
                )}

                <BaseAvatarGroup avatars={jobDetail?.assignees || []} borderColor='#FFF' max={5} />
              </Stack>
            }
          />

          <BaseLabelValueField
            label={t('tab_summary_job_tags')}
            value={
              <>
                {jobDetail?.tags &&
                  jobDetail?.tags?.length > DEFAULT_NUMBER_ZERO &&
                  jobDetail.tags?.map((item) => item?.name).join(', ')}
              </>
            }
          />

          <BaseLabelValueField
            label={t('tab_summary_lead_source')}
            value={formatTitleCase(jobDetail?.leadSource?.name)}
          />

          <BaseLabelValueField
            label={t('tab_summary_job_fields')}
            value={
              <Stack spacing={'8px'}>
                {jobDetail?.type && (
                  <Typography component={'div'} className={cx('text')}>
                    {formatTitleCase(jobDetail?.type)}
                  </Typography>
                )}

                {jobDetail?.unit && (
                  <Typography component={'div'} className={cx('text')}>
                    {jobDetail?.unit !== UnitJobTypeEnum.Plumbing
                      ? jobDetail?.unit
                      : formatTitleCase(jobDetail?.unit || EMPTY_STRING)}
                  </Typography>
                )}

                <FormControlLabel
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  className={cx('formControlLabel')}
                  control={<Checkbox checked={jobDetail?.callback} disabled className={cx('checkbox')} />}
                  label={t('tab_summary_callback')}
                />
              </Stack>
            }
          />

          <BaseLabelValueField
            label={t('tab_summary_commission_rate')}
            value={<>{displayPercentageVal(jobDetail?.schedule?.commissionRate)}</>}
          />

          <BaseLabelValueField
            label={t('tab_summary_review')}
            value={
              jobDetail?.review ? (
                <Stack spacing={'8px'}>
                  <Rating
                    readOnly
                    value={jobDetail?.review?.rating || DEFAULT_NUMBER_ZERO}
                    defaultValue={DEFAULT_NUMBER_ZERO}
                    precision={0.5}
                    size='small'
                  />

                  {jobDetail?.review ? (
                    <BaseTextEditor
                      mode={ModeTextEditorEnum.VIEW}
                      theme={ThemeTextEditorEnum.BUBBLE}
                      value={jobDetail?.review?.content}
                      width={'100%'}
                    />
                  ) : (
                    EMPTY_STRING
                  )}
                </Stack>
              ) : (
                EMPTY_STRING
              )
            }
          />
        </Grid2>

        <Grid2 container direction={'column'} size={6} spacing={0}>
          <Stack className={cx('mapContainer')}>
            <BaseLabelValueField
              label={t('tab_summary_address')}
              value={jobDetail?.address}
              bordered={false}
              labelSize={'auto'}
            />

            <Stack flex={1} className={cx('map')}>
              {isLoaded && (
                <BaseMap
                  {...(jobDetail?.latitude &&
                    jobDetail?.longitude && {
                      position: {
                        lat: jobDetail?.latitude,
                        lng: jobDetail?.longitude,
                      },
                    })}
                />
              )}
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>

      {/* <Box component={'div'} className={cx('viewEdit')} onClick={() => handleClickEditJob()}>
        <img src={icons.commonIconEditPrimary} alt={t('common_alt_img', { ns: 'common/base' })} />
        <Typography component={'p'} className={cx('textValueEdit')}>
          {t('tab_summary_edit_label')}
        </Typography>
      </Box> */}
    </Box>
  );
};

export default JobSummary;
